import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { 
  ArrowLeft, 
  Bell, 
  CheckCircle, 
  AlertCircle, 
  AlertTriangle, 
  Info, 
  User, 
  DollarSign, 
  TrendingUp, 
  Wrench, 
  Shield, 
  Clock, 
  MapPin,
  Activity,
  Zap,
  Car,
  Users,
  FileText,
  Calendar,
  CreditCard,
  AlertOctagon,
  Filter,
  Search,
  X
} from 'lucide-react';
import { apiService } from '../services';

const SuperAdminNotifications = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || 'ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Notification type configurations
  const notificationTypes = {
    SYSTEM_ANNOUNCEMENT: { icon: Info, color: 'text-blue-600', bgColor: 'bg-blue-50', displayName: 'System Announcement' },
    PLATFORM_ANALYTICS: { icon: TrendingUp, color: 'text-purple-600', bgColor: 'bg-purple-50', displayName: 'Platform Analytics' },
    SUPER_ADMIN_BOOKING_RECEIVED: { icon: Bell, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Booking Received' },
    SUPER_ADMIN_DRIVER_ASSIGNED: { icon: User, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Driver Assigned' },
    SUPER_ADMIN_BOOKING_COMPLETED: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Booking Completed' },
    SUPER_ADMIN_DRIVER_ACCEPTED: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Driver Accepted' },
    SUPER_ADMIN_DRIVER_DECLINED: { icon: X, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Driver Declined' },
    SUPER_ADMIN_BOOKING_CANCELLED: { icon: X, color: 'text-red-600', bgColor: 'bg-red-50', displayName: 'Booking Cancelled' },
    SUPER_ADMIN_HOSPITAL_REGISTERED: { icon: AlertCircle, color: 'text-blue-600', bgColor: 'bg-blue-50', displayName: 'Hospital Registered' },
    SUPER_ADMIN_FLEET_REGISTERED: { icon: Car, color: 'text-green-600', bgColor: 'bg-green-50', displayName: 'Fleet Registered' },
    SYSTEM_NOTIFICATION: { icon: Info, color: 'text-blue-600', bgColor: 'bg-blue-50', displayName: 'System Notification' }
  };

  // Fetch notifications
  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.get('/notifications');
      
      if (response.success) {
        const data = response.data || [];
        const allNotifications = Array.isArray(data) ? data : data.notifications || [];
        
        // Filter for Super Admin relevant notifications
        const superAdminNotifications = allNotifications.filter(notification => {
          // Check if it's a SUPER_ADMIN notification by type or metadata
          const isSuperAdminByType = notification.type && (
            notification.type.includes('SUPER_ADMIN') ||
            notification.type.includes('SYSTEM_') ||
            notification.type.includes('PLATFORM_')
          );
          
          // Check if it's a SUPER_ADMIN notification by metadata (workaround)
          const isSuperAdminByMetadata = notification.data && 
            notification.data.superAdminNotificationType;
          
          return isSuperAdminByType || isSuperAdminByMetadata;
        });

        // Filter by selected type
        let filteredNotifications = superAdminNotifications;
        if (selectedType !== 'ALL') {
          filteredNotifications = superAdminNotifications.filter(notification => {
            const actualType = notification.data?.superAdminNotificationType || notification.type;
            return actualType === selectedType;
          });
        }

        // Filter by search query
        if (searchQuery) {
          filteredNotifications = filteredNotifications.filter(notification =>
            notification.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            notification.message.toLowerCase().includes(searchQuery.toLowerCase())
          );
        }

        setNotifications(filteredNotifications);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await apiService.put(`/notifications/${notificationId}/read`);
      
      setNotifications(prev => 
        prev.map(notif => 
          notif.notification_id === notificationId 
            ? { ...notif, read_at: new Date().toISOString() }
            : notif
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.read_at);
      for (const notification of unreadNotifications) {
        await apiService.put(`/notifications/${notification.notification_id}/read`);
      }
      
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, read_at: new Date().toISOString() }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Get notification type configuration
  const getNotificationTypeConfig = (notification) => {
    if (notification.data && notification.data.superAdminNotificationType) {
      const superAdminType = notification.data.superAdminNotificationType;
      return notificationTypes[superAdminType] || notificationTypes.SYSTEM_NOTIFICATION;
    }
    return notificationTypes[notification.type] || notificationTypes.SYSTEM_NOTIFICATION;
  };

  // Format time
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  // Get priority configuration
  const getPriorityConfig = (priority) => {
    const priorityConfig = {
      CRITICAL: { color: 'text-red-600', bgColor: 'bg-red-50' },
      HIGH: { color: 'text-orange-600', bgColor: 'bg-orange-50' },
      NORMAL: { color: 'text-blue-600', bgColor: 'bg-blue-50' },
      LOW: { color: 'text-gray-600', bgColor: 'bg-gray-50' }
    };
    return priorityConfig[priority] || priorityConfig.NORMAL;
  };

  useEffect(() => {
    fetchNotifications();
  }, [selectedType, searchQuery]);

  const unreadCount = notifications.filter(n => !n.read_at).length;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between h-auto sm:h-16 py-4 sm:py-0 gap-4 sm:gap-0">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/super-admin')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Back to Dashboard</span>
                <span className="sm:hidden">Back</span>
              </Button>
              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900">Super Admin Notifications</h1>
                <p className="text-xs sm:text-sm text-gray-500">
                  {unreadCount} unread notifications
                </p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 w-full sm:w-auto">
              {/* Search */}
              <div className="relative flex-1 sm:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search notifications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>
              
              {/* Filter */}
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm min-w-0 sm:w-auto"
              >
                <option value="ALL">All Types</option>
                <option value="SUPER_ADMIN_BOOKING_RECEIVED">Booking Received</option>
                <option value="SUPER_ADMIN_DRIVER_ASSIGNED">Driver Assigned</option>
                <option value="SUPER_ADMIN_BOOKING_COMPLETED">Booking Completed</option>
                <option value="SUPER_ADMIN_HOSPITAL_REGISTERED">Hospital Registered</option>
                <option value="SUPER_ADMIN_FLEET_REGISTERED">Fleet Registered</option>
                <option value="SYSTEM_ANNOUNCEMENT">System Announcement</option>
              </select>
              
              {/* Mark All as Read */}
              {unreadCount > 0 && (
                <Button
                  onClick={markAllAsRead}
                  variant="outline"
                  size="sm"
                  className="whitespace-nowrap"
                >
                  <span className="hidden sm:inline">Mark All as Read</span>
                  <span className="sm:hidden">Mark All</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-gray-500 text-sm">Loading notifications...</p>
            </div>
          </div>
        ) : notifications.length === 0 ? (
          <Card>
            <CardContent className="p-6 sm:p-8 text-center">
              <Bell className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-2">No notifications found</h3>
              <p className="text-sm text-gray-500">
                {selectedType === 'ALL' 
                  ? 'No notifications available at the moment.'
                  : `No ${selectedType.replace(/_/g, ' ').toLowerCase()} notifications found.`
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => {
              const config = getNotificationTypeConfig(notification);
              const priorityConfig = getPriorityConfig(notification.priority);
              const IconComponent = config.icon;
              
              return (
                <Card 
                  key={notification.notification_id}
                  className={`cursor-pointer transition-all hover:shadow-lg hover:border-gray-300 ${
                    !notification.read_at ? 'bg-gradient-to-r from-blue-50/50 to-white border-blue-200' : 'bg-white border-gray-200'
                  }`}
                  onClick={() => markAsRead(notification.notification_id)}
                >
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-1.5 gap-1.5 sm:gap-2">
                        <div className="flex items-start gap-2.5 flex-1 min-w-0">
                          <IconComponent className={`w-4 h-4 sm:w-5 sm:h-5 ${config.color} mt-0.5 flex-shrink-0 opacity-80`} />
                          <div className="flex-1 min-w-0">
                            <h3 className={`text-sm sm:text-base text-gray-900 truncate ${
                              !notification.read_at ? 'font-semibold' : 'font-medium'
                            }`}>
                              {notification.title}
                            </h3>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 ml-6 sm:ml-0">
                          <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${priorityConfig.bgColor} ${priorityConfig.color}`}>
                            {notification.priority}
                          </span>
                          {!notification.read_at && (
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0 animate-pulse"></div>
                          )}
                        </div>
                      </div>
                      
                      <p className="text-xs sm:text-sm text-gray-600 mb-2 line-clamp-2 leading-relaxed ml-6 sm:ml-7">
                        {notification.message}
                      </p>
                      
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs text-gray-500 gap-1 ml-6 sm:ml-7">
                        <span className="flex items-center gap-1.5">
                          <Clock className="w-3 h-3 opacity-60" />
                          {formatTime(notification.created_at)}
                        </span>
                        {notification.read_at && (
                          <span className="text-green-600 text-xs font-medium">✓ Read</span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminNotifications;
